import Axios, { type InternalAxiosRequestConfig } from 'axios'
import CryptoJS from 'crypto-js'

import type { TRefreshResponse } from '@/api/auth/types'
import { env } from '@/env'

const AUTH_ACCESS_TOKEN_STORAGE_KEY = '__app-access-token'
const PROFILE_STORAGE_KEY = '__app-profile-data'
const SESSION_EXPIRED_KEY = '__app-session-expired'

/** Request URL patterns that should NOT trigger refresh token (e.g. auth endpoints) */
const SKIP_REFRESH_URLS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/oauth/simluhtan/callback'
]

/** Public API endpoints — jangan kirim Bearer token agar tetap bisa diakses tanpa login */
const PUBLIC_API_URLS = [
  '/report/pemetaan-wilker',
  '/auth/password/otp',
  '/auth/password/reset'
]

const isRefreshSkipped = (url?: string): boolean =>
  SKIP_REFRESH_URLS.some(path => (url ?? '').includes(path))

const isPublicApiUrl = (url?: string): boolean =>
  PUBLIC_API_URLS.some(path => (url ?? '').includes(path))

/** Tampilkan peringatan session saat sisa waktu di bawah nilai ini */
const SESSION_WARNING_SECONDS = 60

let isRefreshing = false
type QueuedRequest = {
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
}
const failedQueue: QueuedRequest[] = []

const processQueue = (error: unknown | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(undefined)
  })
  failedQueue.length = 0
}

// --- Session expiry helpers ---
const getJwtExp = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

const getTokenSecondsRemaining = (token?: string | null): number | null => {
  const accessToken =
    token ?? localStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
  if (!accessToken) return null

  const exp = getJwtExp(accessToken)
  if (!exp) return null

  return exp - Math.floor(Date.now() / 1000)
}

/**
 * Hook setelah login/refresh. Perpanjangan sesi di menit terakhir ditangani
 * oleh SessionExpiryWarning (modal <60s). Interceptor 401 tetap refresh reaktif.
 */
const scheduleProactiveRefresh = (_token: string) => {
  // no-op: session extension is user-driven via SessionExpiryWarning
}

const clearRefreshTimer = () => {
  // no-op: kept for call-site compatibility (signOut, etc.)
}

const clearSessionAndNotify = () => {
  clearRefreshTimer()
  localStorage.removeItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
  localStorage.removeItem(PROFILE_STORAGE_KEY)
  localStorage.setItem(SESSION_EXPIRED_KEY, Date.now().toString())
}

const clearSessionAndRedirectToLogin = () => {
  clearSessionAndNotify()
  window.location.href = '/auth/sign-in?error=expired'
}
// --- End session expiry helpers ---

const api = Axios.create({
  baseURL: `${env.VITE_SERVER_URL}/api${env.VITE_API_VERSION ? `/${env.VITE_API_VERSION}` : ''}`
})

const encryptData = (plainValue: string, key: string): string => {
  try {
    const keyHash = CryptoJS.SHA256(key)

    const iv = CryptoJS.lib.WordArray.random(16)

    const encrypted = CryptoJS.AES.encrypt(plainValue, keyHash, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    })

    const combined = iv.concat(encrypted.ciphertext)

    return CryptoJS.enc.Base64.stringify(combined)
  } catch (error) {
    console.error('Encrypt error:', error)
    return plainValue
  }
}

const decryptData = (encodedValue: string, key: string) => {
  try {
    const keyHash = CryptoJS.SHA256(key)

    const decodedValue = CryptoJS.enc.Base64.parse(encodedValue)

    const ivLength = 16

    const ivWords = decodedValue.words.slice(0, ivLength / 4)
    const iv = CryptoJS.lib.WordArray.create(ivWords, ivLength)

    const encryptedWords = decodedValue.words.slice(ivLength / 4)
    const encryptedData = CryptoJS.lib.WordArray.create(
      encryptedWords,
      decodedValue.sigBytes - ivLength
    )

    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedData
    })

    const decrypted = CryptoJS.AES.decrypt(cipherParams, keyHash, { iv })

    return decrypted.toString(CryptoJS.enc.Utf8)
  } catch (error) {
    console.error('Decrypt error:', error)
    return encodedValue
  }
}

const isEncrypted = (value: string, secretKey: string): boolean => {
  try {
    const decrypted = decryptData(value, secretKey)
    return decrypted !== value && decrypted.length > 0
  } catch {
    return false
  }
}

const handleEncryptSensitiveFields = (data: any, secretKey: string): any => {
  if (!data) return data

  if (data instanceof FormData) {
    return data
  }

  if (Array.isArray(data)) {
    return data.map(item => handleEncryptSensitiveFields(item, secretKey))
  }

  if (typeof data === 'object' && data !== null) {
    const encryptedData = { ...data }

    if (
      encryptedData.nik &&
      typeof encryptedData.nik === 'string' &&
      !isEncrypted(encryptedData.nik, secretKey)
    ) {
      encryptedData.nik = encryptData(encryptedData.nik, secretKey)
    }

    if (
      encryptedData.nip &&
      typeof encryptedData.nip === 'string' &&
      !isEncrypted(encryptedData.nip, secretKey)
    ) {
      encryptedData.nip = encryptData(encryptedData.nip, secretKey)
    }

    for (const key in encryptedData) {
      if (
        encryptedData[key] &&
        typeof encryptedData[key] === 'object' &&
        !(encryptedData[key] instanceof Date) &&
        !(encryptedData[key] instanceof FormData) &&
        !(encryptedData[key] instanceof File)
      ) {
        encryptedData[key] = handleEncryptSensitiveFields(
          encryptedData[key],
          secretKey
        )
      }
    }

    return encryptedData
  }

  return data
}

api.interceptors.request.use(opts => {
  const token = localStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
  const requestUrl = opts.url ?? ''

  if (token && !isPublicApiUrl(requestUrl)) {
    opts.headers.set('Authorization', `Bearer ${token}`)
    opts.headers.set('ngrok-skip-browser-warning', 'true')
  }

  if (opts.data) {
    const secretKey = env.VITE_APP_SECRET_KEY!
    opts.data = handleEncryptSensitiveFields(opts.data, secretKey)
  }

  return opts
})

api.interceptors.response.use(
  response => {
    if (response.data?.data) {
      const secretKey = env.VITE_APP_SECRET_KEY!
      response.data.data = handleDecryptNoKTP(response.data.data, secretKey)
    }
    return response
  },
  async error => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    if (!Axios.isAxiosError(error)) return Promise.reject(error)

    if (!navigator.onLine) {
      console.log('📴 Offline - request akan dikirim saat online')
    }

    const status = error.response?.status
    const token = localStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)

    // 401: attempt refresh before logout (best practice)
    if (status === 401) {
      // Public endpoints: jangan trigger logout/refresh
      if (isPublicApiUrl(originalRequest?.url)) {
        return Promise.reject(error)
      }

      if (
        isRefreshSkipped(originalRequest?.url) ||
        !token ||
        originalRequest?._retry
      ) {
        clearSessionAndNotify()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // Queue request and wait for refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => api(originalRequest))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data } = await api.post<TRefreshResponse>('/auth/refresh')
        const newToken = data.data?.access_token

        if (newToken) {
          localStorage.setItem(AUTH_ACCESS_TOKEN_STORAGE_KEY, newToken)
          // Update profile cache if backend returns user in refresh response
          if (data.data?.user) {
            try {
              localStorage.setItem(
                PROFILE_STORAGE_KEY,
                JSON.stringify(data.data.user)
              )
            } catch {
              // Ignore storage errors (quota, etc.)
            }
          }
          scheduleProactiveRefresh(newToken)
          originalRequest.headers.set(
            'Authorization',
            `Bearer ${newToken}`
          )
          processQueue(null)
          return api(originalRequest)
        }
      } catch (refreshError) {
        processQueue(refreshError)
        clearSessionAndNotify()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 403: no refresh, logout immediately (kecuali public API)
    if (status === 403 && !isPublicApiUrl(originalRequest?.url)) {
      clearSessionAndNotify()
    }

    return Promise.reject(error)
  }
)

const handleAxiosError = (error: any) => {
  if (Axios.isAxiosError(error)) {
    const status = error.response?.status || error.status

    if (
      (status === 401 || status === 403) &&
      !isPublicApiUrl(error.config?.url)
    ) {
      localStorage.removeItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
      localStorage.setItem(SESSION_EXPIRED_KEY, Date.now().toString())
    }

    const errData = error.response?.data || null
    return {
      status: status || 500,
      data: errData?.data || null,
      message: errData?.message || 'Something went wrong',
      errors: errData?.errors ?? null,
      error: {
        error: errData?.error?.error || null,
        message: errData?.error?.message || null,
        line: errData?.error?.line || null
      }
    }
  }

  return {
    status: 500,
    message: 'Something went wrong'
  }
}

const maskNoKTP = (noKTP: string): string => {
  if (!noKTP || noKTP.length <= 5) return noKTP
  const visibleLength = noKTP.length - 5
  return `${noKTP.substring(0, visibleLength)}*****`
}

const handleDecryptNoKTP = (
  data: any,
  secretKey: string,
  isArrayContext = false
): any => {
  if (!data) return data

  if (Array.isArray(data)) {
    return data.map(item => handleDecryptNoKTP(item, secretKey, true))
  }

  if (typeof data === 'object' && data !== null) {
    const decryptedData = { ...data }

    if (decryptedData.nik && typeof decryptedData.nik === 'string') {
      try {
        const decrypted = decryptData(decryptedData.nik, secretKey)
        if (decrypted && decrypted.length > 0) {
          decryptedData.nik = isArrayContext
            ? maskNoKTP(decrypted)
            : decrypted
        }
      } catch (error) {
        console.error('Error decrypting nik:', error)
      }
    }

    if (decryptedData.nip && typeof decryptedData.nip === 'string') {
      try {
        const decrypted = decryptData(decryptedData.nip, secretKey)
        if (decrypted && decrypted.length > 0) {
          decryptedData.nip = isArrayContext ? maskNoKTP(decrypted) : decrypted
        }
      } catch (error) {
        console.error('Error decrypting nip:', error)
      }
    }

    if (decryptedData.model && typeof decryptedData.model === 'object') {
      if (
        decryptedData.model.code &&
        typeof decryptedData.model.code === 'string' &&
        isEncrypted(decryptedData.model.code, secretKey)
      ) {
        const decrypted = decryptData(decryptedData.model.code, secretKey)
        if (decrypted && decrypted.length > 0) {
          decryptedData.model.code = isArrayContext
            ? maskNoKTP(decrypted)
            : decrypted
        }
      }
    }

    for (const key in decryptedData) {
      if (
        decryptedData[key] &&
        typeof decryptedData[key] === 'object' &&
        !(decryptedData[key] instanceof Date)
      ) {
        decryptedData[key] = handleDecryptNoKTP(
          decryptedData[key],
          secretKey,
          isArrayContext
        )
      }
    }

    return decryptedData
  }

  return data
}

export {
  api,
  AUTH_ACCESS_TOKEN_STORAGE_KEY,
  handleAxiosError,
  SESSION_EXPIRED_KEY,
  SESSION_WARNING_SECONDS,
  scheduleProactiveRefresh,
  clearRefreshTimer,
  getJwtExp,
  getTokenSecondsRemaining,
  clearSessionAndNotify,
  clearSessionAndRedirectToLogin
}
